// @wc-ignore-file
/**
 * CR-202: pure helpers for voice dictation in the chat composer, built on the
 * browser Web Speech API (Chrome, Edge, Safari; Firefox and Brave have none,
 * so the composer simply hides the microphone there). The browser does the
 * speech-to-text through its vendor's speech service (Chrome and Edge send the
 * audio to Google / Microsoft, Safari to Apple); Kairo only ever receives the
 * final text as a normal message.
 *
 * No React: the directive above keeps wuchale away from the error codes and
 * language choices, which are data compared against the browser's values.
 * The only visible strings here are the language names of the dictation
 * picker (CR-202 S4), deliberately untranslated: each language is shown in
 * its own name, as in any language picker.
 *
 * TS lib.dom ships the result types but not the `SpeechRecognition` interface
 * itself, so the minimal shapes the hook needs are declared here.
 */

import { VOICE_DICTATION_LANGUAGE_KEY_PREFIX } from "@/lib/local-storage.consts";

/** One recognised alternative (only the first is ever read). */
export interface SpeechAlternativeLike {
  readonly transcript: string;
}

/** One recognition result: `isFinal` plus its alternatives by index. */
export interface SpeechResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: SpeechAlternativeLike | undefined;
}

/** `event.results`: every result of the current session, finals first. */
export type SpeechResultListLike = ArrayLike<SpeechResultLike>;

export interface SpeechRecognitionResultEventLike {
  readonly results: SpeechResultListLike;
}

export interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}

/** The part of a `SpeechRecognition` instance the dictation hook drives. */
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

/** Where the constructor lives: `window` in the app, a plain object in tests. */
export interface SpeechRecognitionHost {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

/** The unprefixed constructor when present, else Chrome/Safari's prefixed one. */
export function getSpeechRecognitionCtor(
  host: SpeechRecognitionHost | undefined,
): SpeechRecognitionCtor | undefined {
  if (!host) {
    return undefined;
  }
  return host.SpeechRecognition ?? host.webkitSpeechRecognition;
}

export function isSpeechRecognitionSupported(host: SpeechRecognitionHost | undefined): boolean {
  return typeof getSpeechRecognitionCtor(host) === "function";
}

export interface Transcript {
  finalText: string;
  interimText: string;
}

/**
 * Rebuild the session transcript from the whole results list. Recomputing from
 * index 0 on every event (rather than appending from `resultIndex`) keeps it
 * idempotent: Chrome for Android repeats finals in continuous mode.
 */
export function buildTranscript(results: SpeechResultListLike): Transcript {
  let finalText = "";
  let interimText = "";
  for (let index = 0; index < results.length; index += 1) {
    const result = results[index];
    const text = result?.[0]?.transcript ?? "";
    if (result?.isFinal) {
      finalText += text;
    } else {
      interimText += text;
    }
  }
  return { finalText, interimText };
}

/**
 * Join dictated text onto existing text (the draft typed before dictation
 * started, or the text of earlier auto-restarted sessions): no separator after
 * whitespace (space or newline), one space otherwise. An empty transcript
 * leaves the existing text untouched.
 */
export function appendDictatedText(draft: string, transcript: string): string {
  const text = transcript.trim();
  if (!text) {
    return draft;
  }
  if (!draft) {
    return text;
  }
  return /\s$/.test(draft) ? `${draft}${text}` : `${draft} ${text}`;
}

export type RecognitionErrorKind =
  | "permission"
  | "no_speech"
  | "no_microphone"
  | "network"
  | "aborted"
  | "language"
  | "unknown";

/** Map a `SpeechRecognitionErrorEvent.error` code to what the UI tells the user. */
export function mapRecognitionError(code: string): RecognitionErrorKind {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "permission";
    case "no-speech":
      return "no_speech";
    case "audio-capture":
      return "no_microphone";
    case "network":
      return "network";
    case "aborted":
      return "aborted";
    case "language-not-supported":
      return "language";
    default:
      return "unknown";
  }
}

/**
 * The device/browser language, for the "Browser default" choice. Without a
 * lang Chrome falls back to `<html lang>`, which is a static "en" in Kairo.
 */
export function pickRecognitionLang(nav: { language?: string } | undefined): string | undefined {
  const lang = nav?.language?.trim();
  return lang ? lang : undefined;
}

/** CR-202 S4: what the composer's language picker offers. */
export const DICTATION_LANGUAGES = ["en", "fr", "de", "browser"] as const;
export type DictationLanguage = (typeof DICTATION_LANGUAGES)[number];
/** The picker's named languages, in menu order ("browser" comes last). */
export const FIXED_DICTATION_LANGUAGES = ["en", "fr", "de"] as const;

/**
 * Each language's own name, shown as is in every UI language. Keep them here,
 * out of the translated component, so wuchale never translates them.
 */
export const DICTATION_LANGUAGE_NAMES: Record<(typeof FIXED_DICTATION_LANGUAGES)[number], string> =
  {
    en: "English",
    fr: "Français",
    de: "Deutsch",
  };

/** BCP-47 tag handed to the recognizer for each fixed choice. */
const RECOGNITION_TAGS: Record<(typeof FIXED_DICTATION_LANGUAGES)[number], string> = {
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
};

export function parseDictationLanguage(raw: unknown): DictationLanguage | null {
  return typeof raw === "string" && (DICTATION_LANGUAGES as readonly string[]).includes(raw)
    ? (raw as DictationLanguage)
    : null;
}

/** With no stored choice: the Kairo UI language, else the browser's. */
export function defaultDictationLanguage(appLocale: string | null | undefined): DictationLanguage {
  return (FIXED_DICTATION_LANGUAGES as readonly unknown[]).includes(appLocale)
    ? (appLocale as DictationLanguage)
    : "browser";
}

/**
 * The tag for an explicit choice; undefined for "browser", so the hook uses
 * the browser language itself (with its retry-without-lang fallback, which an
 * explicit choice must not get: it would silently transcribe in English).
 */
export function recognitionLangFor(choice: DictationLanguage): string | undefined {
  return choice === "browser" ? undefined : RECOGNITION_TAGS[choice];
}

export function dictationLanguageStorageKey(userId: string): string {
  return `${VOICE_DICTATION_LANGUAGE_KEY_PREFIX}${userId}`;
}

type ChoiceStorage = Pick<Storage, "getItem" | "setItem">;

/** The stored choice, or null when absent, unknown or storage is unavailable. */
export function readStoredDictationLanguage(
  storage: ChoiceStorage | undefined,
  userId: string,
): DictationLanguage | null {
  try {
    return parseDictationLanguage(storage?.getItem(dictationLanguageStorageKey(userId)));
  } catch {
    return null;
  }
}

/** Remember the choice; a blocked or full storage just forgets it. */
export function storeDictationLanguage(
  storage: ChoiceStorage | undefined,
  userId: string,
  choice: DictationLanguage,
): void {
  try {
    storage?.setItem(dictationLanguageStorageKey(userId), choice);
  } catch {
    // Private window or blocked site data: the choice lasts for this page only.
  }
}
